import { useState } from "react";
import { Plus } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PurchaseOrdersTable, PurchaseOrderDialog } from "@/components/PurchaseOrdersTable";
import { useSupplierNames } from "@/components/PurchaseOrdersTable";

// Re-exported for the purchase order detail page, which opens the same dialog.
export { PurchaseOrderDialog } from "@/components/PurchaseOrdersTable";

const ALL_SUPPLIERS = "all";

export function PurchaseOrdersPage({ onSelect }: { onSelect?: (purchaseOrderId: string) => void } = {}) {
  const [createOpen, setCreateOpen] = useState(false);
  // Bumped after a create so the table refetches.
  const [reloadSignal, setReloadSignal] = useState(0);
  const [supplierId, setSupplierId] = useState<string>(ALL_SUPPLIERS);
  const suppliers = useSupplierNames();

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Purchase orders"
        subtitle="Raise a PO, then Dolfin three-way matches it against the supplier's bills automatically."
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            New purchase order
          </Button>
        }
      />

      {/* Only worth showing once there is more than one supplier to tell apart. */}
      {suppliers.size > 1 ? (
        <div className="mb-4 flex justify-end">
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger className="h-9 w-56" aria-label="Filter by supplier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SUPPLIERS}>All suppliers</SelectItem>
              {[...suppliers]
                .sort((a, b) => a[1].localeCompare(b[1]))
                .map(([id, name]) => (
                  <SelectItem key={id} value={id}>
                    {name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <PurchaseOrdersTable
        onSelect={onSelect}
        reloadSignal={reloadSignal}
        supplierId={supplierId === ALL_SUPPLIERS ? undefined : supplierId}
      />

      <PurchaseOrderDialog
        open={createOpen}
        editing={null}
        onOpenChange={setCreateOpen}
        onSaved={() => setReloadSignal((s) => s + 1)}
      />
    </PageContainer>
  );
}
